import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const businesses = [
  {
    name: 'Cafeterias e restaurantes',
    caption: 'O café de todo dia.',
    use: 'Transforme o café de passagem em uma parada de todo dia.',
    example: 'A cada visita, um carimbo. Na próxima recompensa, um café por sua conta.',
    image: '/images/neighborhood-coffee.avif',
    alt: 'Um café sendo entregue de mão em mão no balcão',
  },
  {
    name: 'Barbearias e salões',
    caption: 'O seu lugar de sempre.',
    use: 'Dê ao próximo corte um motivo para já estar marcado.',
    example: 'Recompense a frequência e avise seus clientes das novidades pelo cartão.',
    image: '/images/hero-barbershop.webp',
    alt: 'Barbeiro cumprimenta um cliente após o atendimento',
  },
  {
    name: 'Floriculturas',
    caption: 'Pequenos motivos para voltar.',
    use: 'Esteja presente também entre as datas especiais.',
    example: 'Um programa de pontos para quem gosta de levar flores para casa.',
    image: '/images/business-florist.avif',
    alt: 'Florista preparando um buquê em sua loja',
  },
  {
    name: 'Lojas e boutiques',
    caption: 'Uma escolha que vira hábito.',
    use: 'Faça uma boa descoberta virar a loja de sempre.',
    example: 'Reúna benefícios e saldo de cashback em um cartão com a sua marca.',
    image: '/images/business-boutique.avif',
    alt: 'Lojista dobrando uma peça de linho no balcão',
  },
];

export function BusinessGallery() {
  const galleryRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  function measure() {
    const gallery = galleryRef.current;
    if (gallery)
      setEdges({
        start: gallery.scrollLeft < 2,
        end:
          gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 2,
      });
  }
  useEffect(() => {
    measure();
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (galleryRef.current) observer?.observe(galleryRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  function move(direction: number) {
    const gallery = galleryRef.current;
    if (!gallery) return;
    const width =
      gallery.querySelector<HTMLElement>('.business-card')?.offsetWidth ??
      gallery.clientWidth;
    gallery.scrollBy({
      left: direction * (width + 24),
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }
  return (
    <>
      <div
        className="business-gallery"
        ref={galleryRef}
        id="business-gallery"
        role="region"
        aria-label="Tipos de negócio"
        aria-roledescription="carrossel"
        tabIndex={0}
        onScroll={measure}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          move(event.key === 'ArrowLeft' ? -1 : 1);
        }}
      >
        <ul className="business-track">
          {businesses.map((business, index) => (
            <li className="business-card" key={business.name}>
              <figure>
                <img
                  src={business.image}
                  alt={business.alt}
                  width="1536"
                  height="1024"
                  loading="lazy"
                />
                <figcaption>
                  <span className="business-card__number">0{index + 1}</span>
                  <div>
                    <h3>{business.name}</h3>
                    <p>{business.caption}</p>
                  </div>
                </figcaption>
              </figure>
              <div className="business-use-case">
                <h4>{business.use}</h4>
                <p>{business.example}</p>
                <a href="#modelos">Ver exemplo de cartão <ArrowRight size={16} aria-hidden="true" /></a>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="gallery-bottom">
        <p>Tem lugares que viram parte da nossa vida.</p>
        <div className="gallery-controls">
          <button
            type="button"
            aria-label="Negócios anteriores"
            aria-controls="business-gallery"
            disabled={edges.start}
            onClick={() => move(-1)}
          >
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Próximos negócios"
            aria-controls="business-gallery"
            disabled={edges.end}
            onClick={() => move(1)}
          >
            <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}
